import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import TableDiff from "./SubComponents/TableDiff";
import { useTableActions } from "./SubComponents/hooks/useTableActions";
import HeaderInvoices from "./HeaderInvoices";
import AddInvoiceModal from "./AddModal"; // Unified modal for add/edit
import InvoiceDetailDrawer from "./InvoiceDetailDrawer"; // Keep for view functionality
import {
  clientInvoiceApis,
  clientApis,
  financeSettingsApi,
} from "../../../Data/axios";
import { Box } from "@mui/material";

export default function InvoicesTableUI() {
  const [open, setOpen] = useState(false); // unified modal for add/edit
  const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);

  // State for InvoiceDetailDrawer (view functionality)
  const [showInvoiceDrawer, setShowInvoiceDrawer] = useState(false);
  const [viewInvoiceData, setViewInvoiceData] = useState(null);

  // States for API data
  const [invoiceData, setInvoiceData] = useState([]);
  const [queries, setQueries] = useState({
    page: 1,
    per_page: 10,
  });

  // Filter state
  const [filters, setFilters] = useState({
    dateFilter: "Invoice Date",
    client: "All",
    client_id: null,
    status: "All",
    invoice_number: "",
  });

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    console.log(`Filter changed - ${filterType}: ${value}`);

    if (filterType === "clearAll") {
      // Reset all filters to default values
      setFilters({
        dateFilter: "Invoice Date",
        client: "All",
        client_id: null,
        status: "All",
        invoice_number: "",
      });
      return;
    }

    if (filterType === "dateRange") {
      console.log("Handling date range filter change:", value);
      // Handle both dates at once, remove dateRange entirely
      setFilters((prev) => {
        const { dateRange, ...restFilters } = prev; // Destructure to remove dateRange
        // Remove from_date/to_date if null passed
        if (!value.from_date && !value.to_date) {
          const { from_date, to_date, ...cleared } = restFilters;
          return cleared;
        }
        return {
          ...restFilters,
          from_date: value.from_date,
          to_date: value.to_date,
        };
      });
    } else {
      // Handle other filters normally
      setFilters((prev) => ({
        ...prev,
        [filterType]: value,
      }));
    }
  };

  // Fetch data on component mount
  const fetchData = React.useCallback(
    async (params = {}) => {
      try {
        // Build filter parameters
        const filterParams = {};

        if (filters.invoice_number && filters.invoice_number.trim() !== "") {
          filterParams.invoice_number = filters.invoice_number;
        }

        if (filters.client_id) {
          filterParams.client_id = filters.client_id;
        }

        if (filters.status && filters.status !== "All") {
          // Convert status back to API format (0 for Paid, 1 for Unpaid)
          filterParams.status = filters.status === "Unpaid" ? 1 : 0;
        }

        // Handle date filters - Construct Invoice_date range string
        if (filters.from_date && filters.to_date) {
          filterParams.Invoice_date = `${filters.from_date},${filters.to_date}`;
        } else if (filters.from_date) {
          filterParams.Invoice_date = filters.from_date;
        }

        // Combine all parameters
        const apiParams = {
          page: params.page || queries.page,
          per_page: params.per_page || queries.per_page,
          ...filterParams,
          ...params,
        };

        console.log("API parameters being sent:", apiParams);

        try {
          const invoicesResponse = await clientInvoiceApis.getAll(apiParams);
          console.log("API Response:", invoicesResponse);

          let rawData = [];
          if (invoicesResponse.status === "1600" && invoicesResponse.invoices) {
            // Handle paginated response structure
            if (invoicesResponse.invoices.data) {
              rawData = invoicesResponse.invoices.data;
              // Update pagination queries if needed, mainly page and total
              // But queries state only tracks page/per_page request params
            } else if (Array.isArray(invoicesResponse.invoices)) {
              rawData = invoicesResponse.invoices;
            }
          }

          // Format the data
          const formattedData = rawData.map((invoice) => ({
            ...invoice,
            Invoice_date: invoice.Invoice_date
              ? invoice.Invoice_date.split("T")[0]
              : "",
            due_date: invoice.due_date ? invoice.due_date.split("T")[0] : "",
            status: invoice.status === 1 || invoice.status === "Unpaid" ? "Unpaid" : "Paid",
            client_name: (
              <div className="flex flex-col">
                <Link
                  to={`/clients/${invoice.client_id}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  {invoice.username || "Unknown"}
                </Link>
                <div className="text-sm text-gray-600">
                  {invoice.company_name || ""}
                </div>
                <div className="text-xs text-gray-500">
                  {invoice.client_id || ""}
                </div>
              </div>
            ),
            client_id: invoice.client_id || "",
            total_amount: invoice.total_amount || 0,
            paid_amount: invoice.paid_amount || 0,
            unpaid_amount:
              (invoice.total_amount || 0) - (invoice.paid_amount || 0),
            amount_summary: {
              total: invoice.total_amount || 0,
              paid: invoice.paid_amount || 0,
              unpaid: (invoice.total_amount || 0) - (invoice.paid_amount || 0),
            },
          }));

          console.log("Formatted Invoice Data:", formattedData);
          setInvoiceData(formattedData);
        } catch (apiError) {
          console.error("API call failed:", apiError);
          setInvoiceData([]);
        }
      } catch (error) {
        console.error("Error in fetchData:", error);
        setInvoiceData([]);
      }
    },
    [filters, queries.page, queries.per_page]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle modal open/close with mode
  const handleOpen = () => {
    setModalMode("add");
    setSelectedInvoiceData(null);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  // Handle view invoice - show InvoiceDetailDrawer
  const handleViewInvoice = (invoice) => {
    setViewInvoiceData(invoice);
    setShowInvoiceDrawer(true);
  };

  // Handle close of InvoiceDetailDrawer
  const handleCloseInvoiceDrawer = () => {
    setShowInvoiceDrawer(false);
    setViewInvoiceData(null);
  };

  // Handle edit invoice - show AddInvoiceModal in edit mode
  const handleEditInvoice = (invoice) => {
    setModalMode("edit");
    setSelectedInvoiceData(invoice);
    setOpen(true);
  };

  // Handle edit from within InvoiceDetailDrawer
  const handleEditFromDrawer = () => {
    setShowInvoiceDrawer(false);
    setModalMode("edit");
    setSelectedInvoiceData(viewInvoiceData);
    setOpen(true);
  };

  const tableActions = useTableActions(clientInvoiceApis, fetchData);

  // Handle save invoice - refresh data after adding new invoice
  const handleSaveInvoice = async (response) => {
    try {
      console.log("Invoice saved, refreshing data...", response);
      // Refresh invoice data using the existing fetchData function
      await fetchData();
    } catch (err) {
      console.error("Error refreshing data:", err);
    }
  };

  // Custom action handlers for table
  const handleTableAction = (action, row, headers) => {
    if (action === "View") {
      handleViewInvoice(row);
    } else if (action === "Edit") {
      handleEditInvoice(row);
    } else if (action === "Delete") {
      tableActions.handleActionClick(action, row, headers);
    }
  };

  //table settings - converting existing MUI DataTable columns to headers format
  const headers = [
    { key: "invoice_number", label: "Invoice", sortable: true },
    { key: "client_name", label: "Client", sortable: true },
    { key: "amount_summary", label: "Total Amount", sortable: true },
    { key: "Invoice_date", label: "Invoice Date", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  const handlePageChange = (newPage) => {
    setQueries({ ...queries, page: newPage });
  };

  const handlePerPageChange = (newPerPage) => {
    setQueries({ ...queries, per_page: newPerPage, page: 1 });
  };

  return (
    <div className="w-full">
      <HeaderInvoices
        onFilterChange={handleFilterChange}
        filters={filters}
        onAdd={handleOpen}
      />

      <TableDiff
        headers={headers}
        data={invoiceData}
        rowKey="id"
        defaultSortKey=""
        tableActions={{ ...tableActions, handleActionClick: handleTableAction }}
        tableApis={clientInvoiceApis}
        onTableUpdate={fetchData}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        queries={queries}
        type="invoices"
        tableClassName="invoices-table"
        tableStyle={{ fontSize: "16px", fontFamily: "Ubuntu" }}
      />

      {/* Unified modal for add/edit invoices */}
      <AddInvoiceModal
        open={open}
        onClose={handleClose}
        handleSave={handleSaveInvoice}
        tableApis={clientInvoiceApis}
        clientApis={clientApis}
        financeSettingsApi={financeSettingsApi}
        mode={modalMode}
        invoiceData={selectedInvoiceData}
      />

      {/* InvoiceDetailDrawer for viewing invoices */}
      <InvoiceDetailDrawer
        open={showInvoiceDrawer}
        onClose={handleCloseInvoiceDrawer}
        invoiceData={viewInvoiceData}
        onEdit={handleEditFromDrawer}
      />
    </div>
  );
}
